import React, { useState } from 'react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@components/ui/dropdown-menu'
import { Button } from '@components/ui/button'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '../../../lib/utils'

const OptionType = ({ optionView, options, value, onChange, type }) => {
  const [isOpen, setIsOpen] = useState(false)

  // Normalize options format
  let normalizedOptions = options
  if (options?.length && typeof options[0] === 'string') {
    normalizedOptions = options.map((item) => ({ label: item }))
  }

  // Handle multi-select values
  const isMultiSelect = type === 'multioption'
  const selectedValues = isMultiSelect ? (value ? value.split(',') : []) : [value || '3D']
  const displayValue = isMultiSelect
    ? selectedValues.length > 0
      ? selectedValues.length === 1
        ? selectedValues[0]
        : `${selectedValues.length} selected`
      : 'Select options...'
    : value || 'Select an option...'

  const handleOptionClick = (optionLabel) => {
    if (isMultiSelect) {
      const currentValues = value ? value.split(',') : []
      const newValues = currentValues.includes(optionLabel)
        ? currentValues.filter((v) => v !== optionLabel)
        : [...currentValues, optionLabel]
      onChange(newValues.join(','))
    } else {
      onChange(optionLabel)
      setIsOpen(false)
    }
  }

  return (
    <div className="mb-3">
      {optionView === 'dropdown-view' ? (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-between  focus:!ring-black focus:!ring-1 focus:!ring-offset-1 h-12 text-left"
            >
              <span className="truncate">{displayValue}</span>
              <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[var(--radix-dropdown-menu-trigger-width)] z-[999999] max-h-[300px] overflow-y-auto"
            align="start"
            sideOffset={4}
          >
            {normalizedOptions?.map((item, i) => {
              const isSelected = selectedValues.includes(item.label)
              return (
                <DropdownMenuItem
                  key={`option-${i}`}
                  className={cn('flex items-center gap-3 cursor-pointer  px-3 py-2', isSelected && 'bg-gray-300')}
                  onSelect={(event) => {
                    if (isMultiSelect) {
                      event.preventDefault()
                    }
                    handleOptionClick(item.label)
                  }}
                >
                  {/* Thumbnail preview */}
                  {item.image && (
                    <img src={item.image} alt={item.label} className="h-12 w-12 object-cover rounded flex-shrink-0" />
                  )}

                  <span className="flex-1 text-sm">{item.label}</span>

                  {isSelected && <Check className="h-4 w-4 text-green-500" />}
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <div
          className={`${normalizedOptions?.length > 3 ? 'grid' : 'flex'} gap-[6px] pb-3 overflow-x-auto`}
          style={{
            gridTemplateRows: 'repeat(2, 1fr)',
            gridTemplateColumns: `repeat(${Math.ceil(normalizedOptions?.length / 2)}, minmax(195px, 1fr))`,
          }}
        >
          {normalizedOptions?.map((item, i) => (
            <div
              key={`radio-${i}`}
              onClick={() => handleOptionClick(item.label)}
              className={cn(
                'h-[150px] min-w-[195px] max-w-[210px] flex flex-col items-center justify-center relative cursor-pointer text-xs md:text-base capitalize text-[#797979] gap-1 border rounded-lg transition-all hover:border-gray-400',
                selectedValues.includes(item.label) ? 'border-black border-2' : 'border-gray-200'
              )}
            >
              {selectedValues.includes(item.label) && (
                <div className="w-5 h-5 absolute top-3 right-3 bg-black rounded-full flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
              <img
                src={item.image || '/assets/common/uploadimg.svg'}
                alt={item.label}
                className={cn(item.image ? 'w-full rounded-lg h-[calc(100%-25px)] object-cover' : 'h-[calc(50%)]')}
              />
              <span
                className="h-[20px] w-full text-center break-words text-black text-sm whitespace-nowrap overflow-hidden overflow-ellipsis px-2"
                title={item.label}
              >
                {item.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default OptionType
